import React, { Component } from 'react';

const FILTER_TYPE = {
  TEXT: 'TEXT',
  SELECT: 'SELECT',
  MULTISELECT: 'MULTISELECT',
  NUMBER: 'NUMBER',
  DATE: 'DATE'
};

export const FILTER_DELAY = 500;

function optionsEquals(currOpts, prevOpts) {
  if (Array.isArray(currOpts)) {
    if (currOpts.length === prevOpts.length) {
      for (let i = 0; i < currOpts.length; i += 1) {
        if (
          currOpts[i].value !== prevOpts[i].value ||
          currOpts[i].label !== prevOpts[i].label
        ) {
          return false;
        }
      }
      return true;
    }
    return false;
  }
  const keys = Object.keys(currOpts);
  for (let i = 0; i < keys.length; i += 1) {
    if (currOpts[keys[i]] !== prevOpts[keys[i]]) {
      return false;
    }
  }
  return Object.keys(currOpts).length === Object.keys(prevOpts).length;
}

function getOptionValue(options, key) {
  if (Array.isArray(options)) {
    const result = options
      .filter(({ label }) => label === key)
      .map(({ value }) => value);
    return result[0];
  }
  return options[key];
}

// Helper: find the option key that has the given display value
// e.g., if options = {0: 'NiceKG', 1: 'Other'} and displayValue = 'NiceKG', returns '0'
function findOptionKeyByValue(options, displayValue) {
  if (!options || !displayValue) return '';
  
  if (Array.isArray(options)) {
    const found = options.find(opt => opt.value === displayValue || opt.label === displayValue);
    return found ? found.value : '';
  }
  
  // For object format {0: 'NiceKG', 1: 'Other'}
  for (const key in options) {
    if (options[key] === displayValue) {
      return key;
    }
  }
  return '';
}

class MySelectFilter extends Component {
  constructor(props) {
    super(props);
    this.filter = this.filter.bind(this);
    this.fieldName = props.fieldName;
    this.options = this.getOptions(props);
    const isSelected = getOptionValue(this.options, this.getDefaultValue()) !== undefined;
    this.state = { isSelected };
  }

  componentDidMount() {
    const { column, onFilter, getFilter, defaultValue } = this.props;

    // Ensure the select element's value is synced with defaultValue
    const defaultVal = this.getDefaultValue() || '';
    // Find the option key that matches this display value
    const optionKey = findOptionKeyByValue(this.options, defaultVal);
    this.selectInput.value = optionKey || defaultVal;
    this.setState(() => ({ isSelected: defaultVal !== '' }));

    const value = this.selectInput.value;
    if (value && value !== '') {
      // Handle both customFilter and selectFilter patterns
      const result = onFilter(column, FILTER_TYPE.SELECT, true);
      if (typeof result === 'function') {
        result(value);
      } else {
        onFilter(value);
      }
    }

    // export onFilter function to allow users to access
    if (getFilter) {
      getFilter((filterVal) => {
        this.setState(() => ({ isSelected: filterVal !== '' }));
        const optionKey = findOptionKeyByValue(this.options, filterVal);
        this.selectInput.value = optionKey || filterVal;

        const result = onFilter(column, FILTER_TYPE.SELECT);
        if (typeof result === 'function') {
          result(filterVal);
        } else {
          onFilter(filterVal);
        }
      });
    }
  }

  componentDidUpdate(prevProps) {
    let needFilter = false;
    let filterValue = '';
    const {
      column,
      onFilter,
      defaultValue
    } = this.props;
    const nextOptions = this.getOptions(this.props);
    
    if (defaultValue !== prevProps.defaultValue) {
      // DEFAULT VALUE CHANGED - apply the new filter
      needFilter = true;
      filterValue = defaultValue || '';
      // Sync the default value to the select element
      const optionKey = findOptionKeyByValue(this.options, defaultValue || '');
      // Use setState callback to ensure options are rendered before setting value
      this.setState(
        () => ({ isSelected: defaultValue !== '' }),
        () => {
          this.selectInput.value = optionKey || defaultValue || '';
        }
      );
    } else if (!optionsEquals(nextOptions, this.options)) {
      // OPTIONS CHANGED - update this.options and force re-render
      this.options = nextOptions;
      const currentValue = defaultValue || '';
      const optionKey = findOptionKeyByValue(this.options, currentValue);
      // Force re-render by updating state (even though we're not changing state value)
      // This ensures renderOptions() gets called with updated this.options
      // Use setState callback to set the value AFTER React renders the new options to DOM
      this.setState(
        state => ({ isSelected: state.isSelected }),
        () => {
          // This callback runs after state update and DOM re-render
          this.selectInput.value = optionKey || currentValue;
          
          // If there's a current value (from URL param), apply the filter to the table now that options are loaded
          if (currentValue) {
            const result = onFilter(column, FILTER_TYPE.SELECT);
            if (typeof result === 'function') {
              result(currentValue);
            } else {
              onFilter(currentValue);
            }
          }
        }
      );
      // DO NOT set needFilter = true here!
      // If the value exists as an option now, it will be displayed correctly
      // The existing filter will remain active
    }
    if (needFilter) {
      if (filterValue) {
        // Handle both customFilter and selectFilter patterns
        const result = onFilter(column, FILTER_TYPE.SELECT);
        if (typeof result === 'function') {
          result(filterValue);
        } else {
          onFilter(filterValue);
        }
      }
    }
  }

  getOptions(props) {
    return typeof props.options === 'function' ? props.options(props.column) : props.options;
  }

  getDefaultValue() {
    const { filterState, defaultValue } = this.props;
    if (filterState && typeof filterState.filterVal !== 'undefined') {
      return filterState.filterVal;
    }
    return defaultValue;
  }

  cleanFiltered() {
    const { onFilter, column } = this.props;
    const value = (this.props.defaultValue !== undefined) ? this.props.defaultValue : '';
    this.setState(() => ({ isSelected: value !== '' }));
    this.selectInput.value = value;
    
    // Handle both customFilter and selectFilter patterns
    const result = onFilter(column, FILTER_TYPE.SELECT);
    if (typeof result === 'function') {
      result(value);
    } else {
      onFilter(value);
    }
  }

  applyFilter(value) {
    const { onFilter, column } = this.props;
    
    this.setState(() => ({ isSelected: value !== '' }));
    this.selectInput.value = value;
    
    // Handle both customFilter and selectFilter patterns
    const result = onFilter(column, FILTER_TYPE.SELECT);
    if (typeof result === 'function') {
      result(value);
    } else {
      onFilter(value);
    }
  }

  filter(e) {
    const { value } = e.target;
    this.setState(() => ({ isSelected: value !== '' }));
    const { onFilter, column, fieldName, history, search } = this.props;
    
    // Handle both customFilter (direct call) and selectFilter (curried call) patterns
    if (typeof onFilter === 'function') {
      // Check if it's the curried selectFilter pattern or direct customFilter pattern
      const result = onFilter(column, 'SELECT', false);
      if (typeof result === 'function') {
        // Curried pattern from selectFilter
        result(value);
      } else {
        // Direct pattern from customFilter
        onFilter(value);
      }
    }

    // Update URL if fieldName, history, and search are provided
    if (fieldName && history && search !== undefined) {
      const params = new URLSearchParams(search);
      if (value) {
        params.set(fieldName, value);
      } else {
        params.delete(fieldName);
      }
      history.push(`?${params.toString()}`);
    }
  }

  renderOptions() {
    const optionTags = [];
    const { options } = this;
    const { placeholder, column, withoutEmptyOption } = this.props;
    if (!withoutEmptyOption) {
      optionTags.push((
        <option key="-1" value="">{ placeholder || `Select ${column.text}...` }</option>
      ));
    }
    if (Array.isArray(options)) {
      options.forEach(({ value, label }) =>
        optionTags.push(<option key={ value } value={ value }>{ label }</option>));
    } else {
      Object.keys(options).forEach(key =>
        optionTags.push(<option key={ key } value={ key }>{ options[key] }</option>)
      );
    }
    return optionTags;
  }

  render() {
    const {
      id,
      style,
      className,
      defaultValue,
      onFilter,
      column,
      options,
      comparator,
      withoutEmptyOption,
      caseSensitive,
      getFilter,
      filterState,
      ...rest
    } = this.props;

    const selectClass =
      `filter select-filter form-control ${className} ${this.state.isSelected ? '' : 'placeholder-selected'}`;
    const elmId = `select-filter-column-${column.dataField}${id ? `-${id}` : ''}`;

    return (
      <label
        className="filter-label"
        htmlFor={ elmId }
      >
        <span className="sr-only">Filter by { column.text }</span>
        <select
          { ...rest }
          ref={ n => this.selectInput = n }
          id={ elmId }
          style={ style }
          className={ selectClass }
          onChange={ this.filter }
          onClick={ e => e.stopPropagation() }
          defaultValue={ this.getDefaultValue() || '' }
        >
          { this.renderOptions() }
        </select>
      </label>
    );
  }
}


export default MySelectFilter;